import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
} from '@angular/core';
import * as Color from 'color';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { Nullish } from '@plugins/shared/types/helpers';

import type {
  AnimationVariants,
  FlipVariants,
  IconSizes,
} from '../../public/types';
import { FLIP_CORRESPONDENCE } from '../constants';
import {
  handleAnimationInput,
  handleDegInput,
  handleSizeInput,
} from '../utils/utils';

@Component({
  selector: 'icon-base-icon',
  standalone: true,
  imports: [CommonModule],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseIconComponent {
  protected ref = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input() set size(iconSize: IconSizes | Nullish) {
    handleSizeInput(iconSize, this.#size, this.hostStyle);
  }
  @Input() iconStyle?: Record<string, string>;
  @Input() set color(colorVal: string | Nullish) {
    this.hostStyle.setProperty(
      '--color',
      colorVal ? Color(colorVal).toString() : 'inherit',
    );
  }
  @Input() set rotate(angle: number | string | Nullish) {
    if (angle === undefined || angle === null) return;
    const rotateVal = handleDegInput(angle);
    if (rotateVal) this.hostStyle.setProperty('--rotate', rotateVal);
  }
  @Input() set flip(flipVariant: FlipVariants | Nullish) {
    if (!flipVariant) return;
    this.#flipClass.next(FLIP_CORRESPONDENCE[flipVariant]);
  }

  /** `name-animateOn duration-num(ms|s) delay-num(ms|s) iteration-num|infinite direction | no-animation`*/
  @Input() set animation(animationVariant: AnimationVariants | Nullish) {
    handleAnimationInput(
      animationVariant,
      this.#animationClass,
      this.hostStyle,
    );
  }

  #animationClass = new BehaviorSubject<string>('');
  #flipClass = new BehaviorSubject<string>('');
  #size = new BehaviorSubject<string>('md');
  containerClass$ = combineLatest([this.#size, this.#flipClass]).pipe(
    map((classes) => classes.join(' ')),
  );

  svgClass$ = combineLatest([this.#animationClass]).pipe(
    map((classes) => classes.join(' ')),
  );

  get hostStyle(): CSSStyleDeclaration {
    return this.ref.nativeElement.style;
  }
}
